import { useRef, useState } from "react";
import { ImagePlus, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Spinner } from "./common";

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX = 10 * 1024 * 1024;

export default function PhotoUploader({ photos, onChange }) {
  const { t } = useLang();
  const input = useRef(null);
  const [uploading, setUploading] = useState(0);

  const onFiles = async (files) => {
    const list = Array.from(files);
    let current = [...photos];
    for (const f of list) {
      if (current.length + uploading >= 8) {
        toast.error(t("err.photosMax"));
        break;
      }
      if (!TYPES.includes(f.type)) { toast.error(t("err.fileType")); continue; }
      if (f.size > MAX) { toast.error(t("err.fileSize")); continue; }
      setUploading((n) => n + 1);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd);
        current = [...current, data.path];
        onChange(current);
      } catch {
        toast.error(t("err.upload"));
      }
      setUploading((n) => n - 1);
    }
    if (input.current) input.current.value = "";
  };

  const move = (i, d) => {
    const next = [...photos];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };

  return (
    <div data-testid="photo-uploader">
      <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4">
        {photos.map((p, i) => (
          <div key={p} className="group relative aspect-square overflow-hidden rounded-2xl bg-mist ring-1 ring-slate-200" data-testid={`photo-tile-${i}`}>
            <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
            {i === 0 && (
              <span className="absolute left-1.5 top-1.5 rounded-full bg-navy px-2 py-0.5 text-[10px] font-bold text-white">{t("publish.cover")}</span>
            )}
            <button type="button" onClick={() => onChange(photos.filter((x) => x !== p))}
              className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1 text-alert shadow" aria-label="remove" data-testid={`photo-remove-${i}`}>
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="absolute inset-x-1.5 bottom-1.5 flex justify-between">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)}
                className="rounded-full bg-white/90 p-1 text-navy shadow disabled:opacity-0" aria-label="left" data-testid={`photo-left-${i}`}>
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button type="button" disabled={i === photos.length - 1} onClick={() => move(i, 1)}
                className="rounded-full bg-white/90 p-1 text-navy shadow disabled:opacity-0" aria-label="right" data-testid={`photo-right-${i}`}>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {Array.from({ length: uploading }).map((_, i) => (
          <div key={`u${i}`} className="flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl bg-mist ring-1 ring-slate-200">
            <Spinner />
            <span className="text-[11px] text-slate-500">{t("publish.uploading")}</span>
          </div>
        ))}
        {photos.length + uploading < 8 && (
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-brand/40 bg-brand/5 text-brand transition-colors duration-200 hover:bg-brand/10"
            data-testid="photo-add-btn"
          >
            <ImagePlus className="h-6 w-6" />
            <span className="text-xs font-bold">{t("publish.addPhoto")}</span>
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
        onChange={(e) => onFiles(e.target.files)} data-testid="photo-file-input" />
    </div>
  );
}
